import { v4 } from '@lukeed/uuid';
import { produce } from 'immer';
import { buildCorrelationData } from 'nmr-correlation';
import { predictSpectra } from '../../data/PredictionManager';
import { DefaultTolerance } from '../panels/SummaryPanel/CorrelationTable/Constants';
import { options } from '../toolbar/ToolTypes';
import * as AssignmentsActions from './actions/AssignmentsActions';
import * as CorrelationsActions from './actions/CorrelationsActions';
import * as DatabaseActions from './actions/DatabaseActions';
import { setWidth, handleSetDimensions } from './actions/DimensionsActions';
import * as DomainActions from './actions/DomainActions';
import * as FiltersActions from './actions/FiltersActions';
import * as GlobalActions from './actions/GlobalActions';
import { handleHistoryUndo, handleHistoryRedo, handleHistoryReset, } from './actions/HistoryActions';
import * as IntegralsActions from './actions/IntegralsActions';
import * as LoadActions from './actions/LoadActions';
import * as MoleculeActions from './actions/MoleculeActions';
import * as PeaksActions from './actions/PeaksActions';
import { setKeyPreferencesHandler, applyKeyPreferencesHandler, } from './actions/PreferencesActions';
import * as RangesActions from './actions/RangesActions';
import * as SpectraAnalysisActions from './actions/SpectraAnalysisAction';
import * as SpectrumsActions from './actions/SpectrumsActions';
import * as ToolsActions from './actions/ToolsActions';
import * as ZonesActions from './actions/ZonesActions';
import { DISPLAYER_MODE } from './core/Constants';
import { UNDO, REDO, RESET } from './types/HistoryTypes';
import * as types from './types/Types';
export const rangeStateInit = {
    showMultiplicityTrees: false,
    showRangesIntegrals: true,
    showJGraph: false,
};
export const zoneStateInit = {
    showZones: true,
    showSignals: true,
    showPeaks: true,
};
export function getDefaultViewState() {
    return {
        molecules: {},
        ranges: [],
        zones: [],
        peaks: {},
        spectra: {
            activeSpectra: {},
            activeTab: '',
            showLegend: false,
            selectReferences: {},
        },
        zoom: {
            levels: {},
        },
        verticalAlign: {},
    };
}
export const getInitialState = () => ({
    actionType: '',
    data: [],
    tempData: null,
    xDomain: [],
    yDomain: [],
    yDomains: {},
    xDomains: {},
    originDomain: {
        xDomain: [],
        yDomain: [],
        xDomains: {},
        yDomains: {},
        shareYDomain: false,
    },
    integralsYDomains: {},
    width: 0,
    height: 0,
    margin: {
        top: 10,
        right: 20,
        bottom: 50,
        left: 0,
    },
    mode: 'RTL',
    molecules: [],
    view: getDefaultViewState(),
    history: {
        past: [],
        present: null,
        future: [],
        hasUndo: false,
        hasRedo: false,
    },
    isLoading: false,
    keysPreferences: {},
    displayerMode: DISPLAYER_MODE.DM_1D,
    correlations: {},
    displayerKey: '',
    zoom: {
        history: {},
    },
    overDisplayer: false,
    toolOptions: {
        selectedTool: options.zoom.id,
        selectedOptionPanel: null,
        data: {
            baselineCorrection: {
                options: {},
                zones: [],
            },
            apodizationOptions: {},
            pivot: { value: 0, index: 0 },
            zonesNoiseFactor: 1,
            activeFilterID: null,
            predictionIndex: 0,
        },
    },
    usedColors: { '1d': [], '2d': [] },
});
export const initialState = getInitialState();
export function initState(state) {
    const displayerKey = v4();
    const correlations = buildCorrelationData([], {
        tolerance: DefaultTolerance,
    });
    return {
        ...state,
        correlations,
        displayerKey,
        history: {},
    };
}
export function dispatchMiddleware(dispatch) {
    let usedColors = { '1d': [], '2d': [] };
    return (action) => {
        switch (action.type) {
            case types.INITIATE:
            case types.LOAD_DROP_FILES: {
                action.payload.usedColors = usedColors;
                dispatch(action);
                break;
            }
            case types.PREDICT_SPECTRA: {
                const { mol: { molfile }, options, } = action.payload;
                void predictSpectra(molfile).then((data) => {
                    action.payload = { data, options };
                    dispatch(action);
                }, () => {
                    dispatch(action);
                });
                break;
            }
            default:
                action.usedColors = usedColors;
                dispatch(action);
                break;
        }
    };
}
function innerSpectrumReducer(draft, action) {
    if (![types.LOAD_DROP_FILES, types.INITIATE].includes(action.type)) {
        draft.actionType = action.type;
    }
    switch (action.type) {
        case types.INITIATE:
            return LoadActions.initiate(draft, action);
        case types.LOAD_DROP_FILES:
            return LoadActions.loadDropFiles(draft, action);
        case types.SET_LOADING_FLAG:
            return LoadActions.setIsLoading(draft, action.isLoading);
        case types.ADD_PEAK:
            return PeaksActions.addPeak(draft, action.mouseCoordinates);
        case types.ADD_PEAKS:
            return PeaksActions.addPeaks(draft, action);
        case types.DELETE_PEAK_NOTATION:
            return PeaksActions.deletePeak(draft, action.data);
        case types.AUTO_PEAK_PICKING:
            return PeaksActions.handleAutoPeakPicking(draft, action.options);
        case types.OPTIMIZE_PEAKS:
            return PeaksActions.handleOptimizePeaks(draft, action);
        case types.CHANGE_PEAK_SHAPE:
            return PeaksActions.changePeakShapeHandler(draft, action);
        case types.TOGGLE_PEAKS_VIEW_PROPERTY:
            return PeaksActions.handleTogglePeaksViewProperty(draft, action);
        case types.ADD_INTEGRAL:
            return IntegralsActions.addIntegral(draft, action);
        case types.DELETE_INTEGRAL:
            return IntegralsActions.deleteIntegral(draft, action);
        case types.CHANGE_INTEGRAL_DATA:
            return IntegralsActions.changeIntegral(draft, action);
        case types.RESIZE_INTEGRAL:
            return IntegralsActions.changeIntegral(draft, action);
        case types.CHANGE_INTEGRAL_SUM:
            return IntegralsActions.handleChangeIntegralSum(draft, action.value);
        case types.CHANGE_INTEGRALS_SUM_FLAG:
            return IntegralsActions.handleChangeIntegralsSumFlag(draft, action);
        case types.CHANGE_INTEGRAL_RELATIVE:
            return IntegralsActions.handleChangeIntegralsRelativeValue(draft, action);
        case types.SET_ORIGINAL_DOMAIN:
            return DomainActions.setOriginalDomain(draft, action.domain);
        case types.SET_X_DOMAIN:
            return DomainActions.setXDomain(draft, action.xDomain);
        case types.SET_Y_DOMAIN:
            return DomainActions.setYDomain(draft, action.yDomain);
        case types.SET_WIDTH:
            return setWidth(draft, action.width);
        case types.SET_DIMENSIONS:
            return handleSetDimensions(draft, action.width, action.height);
        case types.SET_SELECTED_TOOL:
            return ToolsActions.setSelectedTool(draft, action);
        case types.RESET_SELECTED_TOOL:
            return ToolsActions.resetSelectedTool(draft);
        case types.SET_SELECTED_OPTIONS_PANEL:
            return ToolsActions.setSelectedOptionPanel(draft, action.selectedOptionPanel);
        case types.FULL_ZOOM_OUT:
            return ToolsActions.zoomOut(draft, action);
        case types.DISABLE_FILTER_LIVE_PREVIEW:
            return FiltersActions.handleDisableFilterLivePreview(draft, action);
        case types.SHIFT_SPECTRUM:
            return FiltersActions.shiftSpectrumAlongXAxis(draft, action.shiftValue);
        case types.APPLY_APODIZATION_FILTER:
            return FiltersActions.applyApodizationFilter(draft, action);
        case types.CALCULATE_APODIZATION_FILTER:
            return FiltersActions.calculateApodizationFilter(draft, action);
        case types.APPLY_ZERO_FILLING_FILTER:
            return FiltersActions.applyZeroFillingFilter(draft, action);
        case types.CALCULATE_ZERO_FILLING_FILTER:
            return FiltersActions.calculateZeroFillingFilter(draft, action);
        case types.APPLY_FFT_FILTER:
            return FiltersActions.applyFFTFilter(draft);
        case types.APPLY_MANUAL_PHASE_CORRECTION_FILTER:
            return FiltersActions.applyManualPhaseCorrectionFilter(draft, action.value);
        case types.APPLY_AUTO_PHASE_CORRECTION_FILTER:
            return FiltersActions.applyAutoPhaseCorrectionFilter(draft);
        case types.APPLY_ABSOLUTE_FILTER:
            return FiltersActions.applyAbsoluteFilter(draft);
        case types.CALCULATE_MANUAL_PHASE_CORRECTION_FILTER:
            return FiltersActions.calculateManualPhaseCorrection(draft, action.value);
        case types.ENABLE_FILTER:
            return FiltersActions.enableFilter(draft, action.id, action.checked);
        case types.DELETE_FILTER:
            return FiltersActions.deleteFilter(draft, action);
        case types.DELETE_SPECTRA_FILTER:
            return FiltersActions.deleteSpectraFilter(draft, action);
        case types.SET_FILTER_SNAPSHOT:
            return FiltersActions.filterSnapshotHandler(draft, action);
        case types.APPLY_MULTIPLE_SPECTRA_FILTER:
            return FiltersActions.handleMultipleSpectraFilter(draft, action);
        case types.APPLY_SIGNAL_PROCESSING_FILTER:
            return FiltersActions.handleSignalProcessingFilter(draft, action);
        case types.ADD_EXCLUSION_ZONE:
            return FiltersActions.handleAddExclusionZone(draft, action);
        case types.DELETE_EXCLUSION_ZONE:
            return FiltersActions.handleDeleteExclusionZone(draft, action);
        case types.APPLY_BASE_LINE_CORRECTION_FILTER:
            return FiltersActions.handleBaseLineCorrectionFilter(draft, action);
        case types.CALCULATE_BASE_LINE_CORRECTION_FILTER:
            return FiltersActions.calculateBaseLineCorrection(draft, action);
        case types.CHANGE_VISIBILITY:
            return SpectrumsActions.handleSpectrumVisibility(draft, action);
        case types.CHANGE_ACTIVE_SPECTRUM:
            return SpectrumsActions.handleChangeActiveSpectrum(draft, action);
        case types.CHANGE_SPECTRUM_COLOR:
            return SpectrumsActions.handleChangeSpectrumColor(draft, action.data);
        case types.CHANGE_SPECTRUM_SETTING:
            return SpectrumsActions.changeSpectrumSetting(draft, action);
        case types.ALIGN_SPECTRA:
            return SpectrumsActions.alignSpectraHandler(draft, action);
        case types.DELETE_SPECTRA:
            return SpectrumsActions.handleDeleteSpectra(draft, action);
        case types.ADD_MISSING_PROJECTION:
            return SpectrumsActions.addMissingProjectionHandler(draft, action);
        case types.GENERATE_SPECTRUM_FROM_PUBLICATION_STRING:
            return SpectrumsActions.generateSpectrumFromPublicationStringHandler(draft, action);
        case types.IMPORT_SPECTRA_META_INFO:
            return SpectrumsActions.importSpectraMetaInfo(draft, action);
        case types.TOGGLE_SPECTRA_LEGEND:
            return SpectrumsActions.handleToggleSpectraLegend(draft);
        case types.RECOLOR_SPECTRA_COLOR:
            return SpectrumsActions.handleRecolorSpectraBasedOnDistinctValue(draft, action);
        case types.ORDER_SPECTRA:
            return SpectrumsActions.handleOrderSpectra(draft, action);
        case types.TOGGLE_REAL_IMAGINARY_VISIBILITY:
            return ToolsActions.handleToggleRealImaginaryVisibility(draft);
        case types.SET_ZOOM:
            return ToolsActions.handleZoom(draft, action);
        case types.SET_SPECTRA_SAME_TOP:
            return ToolsActions.setSpectraSameTopHandler(draft);
        case types.RESET_SPECTRA_SCALE:
            return ToolsActions.resetSpectraScale(draft);
        case types.CHANGE_SPECTRUM_DISPLAY_VIEW_MODE:
            return ToolsActions.handleChangeSpectrumDisplayMode(draft);
        case types.ADD_MOLECULE:
            return MoleculeActions.addMoleculeHandler(draft, action.molfile);
        case types.SET_MOLECULE:
            return MoleculeActions.setMoleculeHandler(draft, action);
        case types.CHANGE_MOLECULE_LABEL:
            return MoleculeActions.changeMoleculeLabel(draft, action);
        case types.DELETE_MOLECULE:
            return MoleculeActions.deleteMoleculeHandler(draft, action);
        case types.PREDICT_SPECTRA:
            return MoleculeActions.predictSpectraFromMoleculeHandler(draft, action);
        case types.FLOAT_MOLECULE_OVER_SPECTRUM:
            return MoleculeActions.floatMoleculeOverSpectrum(draft, action);
        case types.TOGGLE_MOLECULE_ATOM_NUMBER:
            return MoleculeActions.toggleMoleculeAtomsNumbers(draft, action);
        case types.CHANGE_FLOAT_MOLECULE_POSITION:
            return MoleculeActions.changeFloatMoleculePosition(draft, action);
        case types.SET_CORRELATIONS_MF:
            return CorrelationsActions.handleSetMF(draft, action.payload);
        case types.SET_CORRELATIONS_TOLERANCE:
            return CorrelationsActions.handleSetTolerance(draft, action.payload);
        case types.SET_CORRELATION:
            return CorrelationsActions.handleSetCorrelation(draft, action.payload);
        case types.SET_CORRELATIONS:
            return CorrelationsActions.handleSetCorrelations(draft, action.payload);
        case types.DELETE_CORRELATION:
            return CorrelationsActions.handleDeleteCorrelation(draft, action.payload);
        case types.BRUSH_END:
            return ToolsActions.handleBrushEnd(draft, action);
        case types.SET_VERTICAL_INDICATOR_X_POSITION:
            return ToolsActions.setVerticalIndicatorXPosition(draft, action.position);
        case types.SET_SPECTRUMS_VERTICAL_ALIGN:
            return ToolsActions.setSpectrumsVerticalAlign(draft);
        case types.AUTO_ZONES_DETECTION:
            return ZonesActions.handleAutoZonesDetection(draft, action.options);
        case types.CHANGE_ZONES_NOISE_FACTOR:
            return ZonesActions.changeZonesFactorHandler(draft, action);
        case types.AUTO_RANGES_DETECTION:
            return RangesActions.handleAutoRangesDetection(draft, action.options);
        case types.ADD_RANGE:
            return RangesActions.handleAddRange(draft, action);
        case types.DELETE_RANGE:
            return RangesActions.handleDeleteRange(draft, action);
        case types.DELETE_1D_SIGNAL:
            return RangesActions.handleDeleteSignal(draft, action);
        case types.RESIZE_RANGE:
            return RangesActions.handleResizeRange(draft, action);
        case types.CHANGE_RANGE_SUM:
            return RangesActions.handleChangeRangeSum(draft, action.value);
        case types.CHANGE_RANGES_SUM_FLAG:
            return RangesActions.handleChangeRangesSumFlag(draft, action);
        case types.CHANGE_RANGE_RELATIVE:
            return RangesActions.handleChangeRangeRelativeValue(draft, action);
        case types.CHANGE_RANGE_SIGNAL_VALUE:
            return RangesActions.handleChangeRangeSignalValue(draft, action);
        case types.CHANGE_RANGE_SIGNAL_KIND:
            return RangesActions.handleChangeRangeSignalKind(draft, action);
        case types.SAVE_EDITED_RANGE:
            return RangesActions.handleSaveEditedRange(draft, action);
        case types.UNLINK_RANGE:
            return RangesActions.handleUnlinkRange(draft, action);
        case types.SET_DIAID_RANGE:
            return RangesActions.handleSetDiaIDRange(draft, action);
        case types.UPDATE_RANGE:
            return RangesActions.handleUpdateRange(draft, action);
        case types.SHOW_MULTIPLICITY_TREES:
            return RangesActions.handleShowMultiplicityTrees(draft, action);
        case types.SHOW_RANGES_INTEGRALS:
            return RangesActions.handleShowRangesIntegrals(draft, action);
        case types.AUTO_RANGES_SPECTRA_PICKING:
            return RangesActions.handleAutoSpectraRangesDetection(draft);
        case types.SHOW_J_GRAPH:
            return RangesActions.handleShowJGraph(draft, action);
        case types.SET_ACTIVE_TAB:
            return ToolsActions.handelSetActiveTab(draft, action.tab);
        case types.ADD_BASE_LINE_ZONE:
            return ToolsActions.handleAddBaseLineZone(draft, action.zone);
        case types.DELETE_BASE_LINE_ZONE:
            return ToolsActions.handleDeleteBaseLineZone(draft, action.id);
        case types.SET_KEY_PREFERENCES:
            return setKeyPreferencesHandler(draft, action.keyCode);
        case types.APPLY_KEY_PREFERENCES:
            return applyKeyPreferencesHandler(draft, action.keyCode);
        case types.SET_2D_LEVEL:
            return ToolsActions.levelChangeHandler(draft, action);
        case types.ADD_2D_ZONE:
            return ZonesActions.add2dZoneHandler(draft, action);
        case types.DELETE_2D_ZONE:
            return ZonesActions.handleDeleteZone(draft, action);
        case types.DELETE_2D_SIGNAL:
            return ZonesActions.handleDeleteSignal(draft, action);
        case types.SET_2D_SIGNAL_PATH_LENGTH:
            return ZonesActions.handleSetSignalPathLength(draft, action);
        case types.RESET_DOMAIN:
            return DomainActions.handelResetDomain(draft);
        case types.CHANGE_ZONE_SIGNAL_VALUE:
            return ZonesActions.changeZoneSignalDelta(draft, action);
        case types.CHANGE_ZONE_SIGNAL_KIND:
            return ZonesActions.handleChangeZoneSignalKind(draft, action);
        case types.UNLINK_ZONE:
            return ZonesActions.handleUnlinkZone(draft, action);
        case types.SET_DIAID_ZONE:
            return ZonesActions.handleSetDiaIDZone(draft, action);
        case types.AUTO_ZONES_SPECTRA_PICKING:
            return ZonesActions.handleAutoSpectraZonesDetection(draft);
        case types.SHOW_ZONES:
            return ZonesActions.handleShowZones(draft, action);
        case types.SHOW_ZONES_SIGNALS:
            return ZonesActions.handleShowSignals(draft, action);
        case types.SHOW_ZONES_PEAKS:
            return ZonesActions.handleShowPeaks(draft, action);
        case types.SAVE_EDITED_ZONE:
            return ZonesActions.handleSaveEditedZone(draft, action);
        case types.ORDER_MULTIPLE_SPECTRA_ANALYSIS:
            return SpectraAnalysisActions.handleOrderSpectra(draft, action);
        case types.RESURRECTING_SPECTRUM_FROM_RANGES:
            return DatabaseActions.handleResurrectSpectrumFromRanges(draft, action);
        case types.RESURRECTING_SPECTRUM_FROM_JCAMP:
            return DatabaseActions.handleResurrectSpectrumFromJcamp(draft, action);
        case types.SET_AUTOMATIC_ASSIGNMENTS:
            return AssignmentsActions.setAutomaticAssignmentsHandler(draft, action);
        case UNDO:
            return handleHistoryUndo(draft);
        case REDO:
            return handleHistoryRedo(draft);
        case RESET:
            return handleHistoryReset(draft, action);
        case types.SET_MOUSE_OVER_DISPLAYER:
            return GlobalActions.setIsOverDisplayer(draft, action);
        default:
    }
}
export const spectrumReducer = produce(innerSpectrumReducer);
